import { useState } from 'react'
import { useParams, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { adminService, User } from '../services/adminService'
import { UserForm } from '../components/UserForm'
import { DeleteConfirmationModal } from '../components/DeleteConfirmationModal'
import { useTranslation } from '../i18n/hooks/useTranslation'
import { Button } from '../components/ui/Button'

export function UserEditPage() {
  const { t } = useTranslation()
  const params = useParams({ from: '/users/$userId' })
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const userId = params.userId

  const { data: user, isLoading, error } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => adminService.getUser(userId),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<User> }) =>
      adminService.updateUser(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['user', userId] })
      toast.success(t('users.notifications.updatedSuccess'))
      navigate({ to: '/users' })
    },
    onError: (error) => {
      console.error('User update error:', error)
      toast.error(t('users.notifications.updateFailed'))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success(t('users.notifications.deletedSuccess'))
      navigate({ to: '/users' })
    },
    onError: (error) => {
      console.error('User delete error:', error)
      toast.error(t('users.notifications.deleteFailed'))
    },
  })

  const handleSubmit = (data: Partial<User>) => {
    if (userId) {
      updateMutation.mutate({ id: userId, updates: data })
    }
  }

  const handleDelete = () => {
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = () => {
    if (userId) {
      deleteMutation.mutate(userId)
      setShowDeleteConfirm(false)
    }
  }

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false)
  }

  const handleCancel = () => {
    navigate({ to: '/users' })
  }

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">{t('common.loading')}</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">{t('errors.loadingUser')}</h3>
          <p className="text-red-600 text-sm mt-1">{error.message}</p>
          <div className="mt-4">
            <Button onClick={() => navigate({ to: '/users' })} variant="secondary">
              {t('common.goBack')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (!user && !isLoading) {
    return (
      <div className="p-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="text-yellow-800 font-medium">{t('users.userNotFound')}</h3>
          <p className="text-yellow-600 text-sm mt-1">{t('users.userNotFoundDescription')}</p>
          <div className="mt-4">
            <Button onClick={() => navigate({ to: '/users' })} variant="secondary">
              {t('common.goBack')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-8">
      {/* Enhanced Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Left side - Back button and title */}
          <div className="flex flex-col items-start gap-2">
            <button
              onClick={() => navigate({ to: '/users' })}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 border border-gray-200 bg-gray-50 rounded-lg transition-all duration-200 group"
              aria-label={t('common.back')}
            >
              <svg 
                className="w-5 h-5 transition-transform group-hover:-translate-x-1" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="font-medium">{t('common.back')}</span>
            </button>
            
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
                {t('users.editUser')}
              </h1>
              <p className="text-gray-600 mt-2 text-sm lg:text-base leading-relaxed">
                {t('users.editSubtitle', { defaultValue: 'Manage user account and permissions' })}
              </p>
            </div>
          </div>

          {/* Right side - Action buttons */}
          <button
            onClick={handleDelete}
            className="flex items-center justify-center gap-2 px-4 py-2.5 text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50 hover:border-red-400 transition-all duration-200 font-medium text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            {t('users.deleteUser')}
          </button>
        </div>
      </div>

      <UserForm
        user={user}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onDelete={handleDelete}
        isLoading={updateMutation.isPending}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('users.deleteUser')}
        message={t('users.actions.deleteConfirm', { name: user?.displayName || user?.email })}
        warningText={t('users.actions.deleteWarning')}
        isLoading={deleteMutation.isPending}
        destructive={true}
      />
    </div>
  )
}